import React, { KeyboardEvent, useEffect, useRef } from "react";
import {
  FileInfo,
  FileName,
  FilePreviewContainer,
  FilePreviewItem,
  FileSize,
  FormWrapper,
  InputContainer,
  InputWrapper,
  RemoveFileButton,
  TextArea,
  TextAreaWrapper,
} from "./__styles__/ChatInput";
import ChatInputButtonGroup from "./ChatInputButtonGroup";

export interface ChatInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  files?: File[];
  onFilesAdd?: (files: File[]) => void;
  onFileRemove?: (file: File) => void;
  audioRecordingStatus?: "idle" | "permission-pending" | "recording";
  recordingTime?: number;
  isTranscribing?: boolean;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onCancelRecording?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  placeholder = "Let's get started...",
  files = [],
  onFilesAdd,
  onFileRemove,
  audioRecordingStatus = "idle",
  recordingTime = 0,
  isTranscribing = false,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to resize textarea based on content
  const resizeTextArea = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set height to scrollHeight to fit content
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
  };

  // Auto-resize textarea when value changes externally (e.g., from transcription)
  useEffect(() => {
    if (textAreaRef.current) {
      resizeTextArea(textAreaRef.current);
    }
  }, [value]);

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true }),
      );
    }
  };

  // Auto-resize textarea based on content
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    onChange(e);
    resizeTextArea(textarea);
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0 && onFilesAdd) {
      onFilesAdd(selectedFiles);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = "";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <FormWrapper
      ref={formRef}
      onSubmit={e => {
        e.preventDefault();
        if (!disabled) {
          onSubmit(e);
        }
      }}
    >
      <InputContainer>
        <InputWrapper>
          {files.length > 0 && (
            <FilePreviewContainer>
              {files.map((file, index) => (
                <FilePreviewItem key={`${file.name}-${file.size}-${index}`}>
                  <FileInfo>
                    <FileName>{file.name}</FileName>
                    <FileSize>{formatFileSize(file.size)}</FileSize>
                  </FileInfo>
                  {onFileRemove && (
                    <RemoveFileButton
                      type="button"
                      onClick={() => onFileRemove(file)}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </RemoveFileButton>
                  )}
                </FilePreviewItem>
              ))}
            </FilePreviewContainer>
          )}
          <TextAreaWrapper>
            <TextArea
              ref={textAreaRef}
              value={value}
              onChange={handleTextAreaChange}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
            />
          </TextAreaWrapper>
          <ChatInputButtonGroup
            disabled={disabled}
            hasContent={!!value.trim()}
            hasFiles={files.length > 0}
            audioRecordingStatus={audioRecordingStatus}
            recordingTime={recordingTime}
            isTranscribing={isTranscribing}
            fileInputRef={fileInputRef}
            onFileButtonClick={handleFileButtonClick}
            onFileChange={handleFileChange}
            onStartRecording={onStartRecording}
            onStopRecording={onStopRecording}
            onCancelRecording={onCancelRecording}
          />
        </InputWrapper>
      </InputContainer>
    </FormWrapper>
  );
};

export default ChatInput;
